import React from 'react';

import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Theme, alpha } from '@mui/material/styles';

type Props = {
  /** 実行可能拡張子か（java 等） */
  runnable: boolean;
  /** md / knz（表示モードを持つ）か */
  supportsViewMode: boolean;
  /** 保存ボタンを活性にするか（編集可能かつ未保存） */
  canSave: boolean;
  /** 実行中/保存中 */
  busy: boolean;
  onSave: () => void;
  onRun: () => void;
  /** 実行ボタンの左隣に表示する要素（実行時引数チップなど）。実行可能拡張子時のみ表示される */
  runExtra?: React.ReactNode;
  /** 記事化済み knz のとき `[記事に反映]` を表示する（R-11・API6211） */
  onReflectArticle?: () => void;
  /** 設問化済み knz のとき `[設問に反映]` を表示する（R-11・API6316） */
  onReflectQuestion?: () => void;
};

/** フローティング用の半透明サーフェス（テーマ背景色 + ぼかし + ソフトシャドウ） */
// このアプリのテーマは cssVariables（colorSchemes）モードのため、sx コールバックの
// t.palette.mode は常に既定値になる。dark 判定は theme.applyStyles('dark', ...) を使う。
const surfaceSx = (t: Theme) => ({
  border: 1,
  borderColor: 'divider',
  // 既定（light）: 背景色（paper）ベースの半透明。
  bgcolor: alpha(t.palette.background.paper, 0.75),
  backdropFilter: 'blur(10px)',
  boxShadow: `0 8px 28px ${alpha(t.palette.common.black, 0.14)}`,
  // dark: ほぼ黒（common.black）ベース + 濃いめの影に上書き。
  ...t.applyStyles('dark', {
    bgcolor: alpha(t.palette.common.black, 0.8),
    boxShadow: `0 8px 28px ${alpha(t.palette.common.black, 0.55)}`,
  }),
});

/**
 * 右ペイン右下のフローティングアクション群。
 * 役割ごとに形を分ける:
 * - 保存: 独立した円形アイコンボタン（未保存時のみ活性・控えめ）
 * - 実行系: 引数チップ + 実行を 1 本の pill にまとめる（実行が主役）
 */
const FloatingActionButtons: React.FC<Props> = ({
  runnable,
  supportsViewMode,
  canSave,
  busy,
  onSave,
  onRun,
  runExtra,
  onReflectArticle,
  onReflectQuestion,
}) => {
  const showRun = !supportsViewMode && runnable;

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 24,
        right: 32,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
      }}
    >
      {/* 反映系: 記事 / 設問（該当時のみ・保存とは別操作） */}
      {onReflectQuestion && (
        <Tooltip title="設問に反映" arrow>
          <Button
            variant="outlined"
            startIcon={<QuizOutlinedIcon />}
            onClick={onReflectQuestion}
            disabled={busy}
            sx={(t) => ({
              height: 44,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 700,
              px: 2,
              ...surfaceSx(t),
            })}
          >
            設問に反映
          </Button>
        </Tooltip>
      )}
      {onReflectArticle && (
        <Tooltip title="記事に反映" arrow>
          <Button
            variant="outlined"
            startIcon={<ArticleOutlinedIcon />}
            onClick={onReflectArticle}
            disabled={busy}
            sx={(t) => ({
              height: 44,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 700,
              px: 2,
              ...surfaceSx(t),
            })}
          >
            記事に反映
          </Button>
        </Tooltip>
      )}

      {/* 保存: 独立した円形アイコンボタン */}
      <Tooltip title="保存" arrow>
        <Box component="span">
          <IconButton
            onClick={onSave}
            disabled={!canSave || busy}
            aria-label="保存"
            sx={(t) => ({
              width: 44,
              height: 44,
              color: 'text.secondary',
              ...surfaceSx(t),
              transition: 'transform 0.15s, color 0.15s',
              '&:hover': { color: 'primary.main', transform: 'translateY(-1px)' },
              '&.Mui-disabled': { color: 'text.disabled' },
            })}
          >
            <SaveOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Tooltip>

      {/* 実行系: 引数チップ + 実行 を 1 本の pill に */}
      {showRun && (
        <Box
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            height: 44,
            pl: 1,
            pr: 0.5,
            borderRadius: 999,
            ...surfaceSx(t),
          })}
        >
          {runExtra}
          <Divider orientation="vertical" flexItem sx={{ my: 1.25 }} />
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayArrowIcon />}
            onClick={onRun}
            disabled={busy}
            sx={{
              height: 34,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 700,
              px: 2.25,
              boxShadow: (t) => `0 4px 14px ${alpha(t.palette.success.main, 0.4)}`,
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: (t) => `0 7px 18px ${alpha(t.palette.success.main, 0.5)}`,
              },
            }}
          >
            実行
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FloatingActionButtons;
